import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function SettingGroup({ groupKey, label, defaults, value, onSave }) {
  const [form, setForm] = useState({ ...defaults, ...(value || {}) });
  return (
    <Card>
      <CardHeader><CardTitle>{label}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(form).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <Label className="text-sm capitalize">{k.replace(/_/g, " ")}</Label>
            {typeof v === "boolean"
              ? <Switch checked={v} onCheckedChange={(b) => setForm({ ...form, [k]: b })} data-testid={`set-${groupKey}-${k}`} />
              : <Input value={v ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="max-w-sm" data-testid={`set-${groupKey}-${k}`} />}
          </div>
        ))}
        <div className="text-right"><Button onClick={() => onSave(groupKey, form)} data-testid={`set-${groupKey}-save`}>Save</Button></div>
      </CardContent>
    </Card>
  );
}

const GROUPS = [
  { key: "branding",      label: "Platform branding",  defaults: { product_name: "TileERP", company_name: "Projexino", primary_color: "#C85A32", accent_color: "#8F9E8B", footer_text: "" } },
  { key: "white_label",   label: "White-label",        defaults: { allow_tenant_logo: true, allow_tenant_colors: true, hide_powered_by: false } },
  { key: "regional",      label: "Regional",           defaults: { default_timezone: "Asia/Kolkata", default_currency: "INR", default_language: "en-IN", week_starts_on: "Mon" } },
  { key: "feature_flags", label: "Feature flags",      defaults: { ai_assistants: true, marketplace: true, subscriptions: true, workflow_engine: false, chat: false } },
  { key: "backup",        label: "Backup & restore",   defaults: { daily_backup_hour_utc: "02", retention_days: 30 } },
  { key: "maintenance",   label: "Maintenance mode",   defaults: { enabled: false, banner_message: "" } },
  { key: "license",       label: "License keys",       defaults: { platform_license: "", expires_on: "" } },
];

export default function GlobalSettings() {
  const [all, setAll] = useState({});
  const load = async () => { const { data } = await api.get("/admin/settings"); setAll(data || {}); };
  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    try { await api.put(`/admin/settings/${key}`, value); toast.success(`${key} saved`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-global-settings">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Global settings</h1>
        <p className="text-muted-foreground">Platform-wide branding, regional defaults, feature flags, backups, license keys and maintenance mode.</p>
      </div>
      <Tabs defaultValue="branding">
        <TabsList className="flex-wrap h-auto">
          {GROUPS.map((g) => <TabsTrigger key={g.key} value={g.key} data-testid={`gs-tab-${g.key}`}>{g.label}</TabsTrigger>)}
        </TabsList>
        {GROUPS.map((g) => (
          <TabsContent key={g.key} value={g.key}>
            <SettingGroup groupKey={g.key} label={g.label} defaults={g.defaults} value={all[g.key]} onSave={save} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
